import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ImagePlus, Loader2, Plus, Trash2 } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea, ScrollArea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import {
  uploadTemplateImage, useSubmitZnsTemplate, useTemplateSpec, validateTemplateDesign,
  type ZnsTemplate,
} from '@/hooks/use-zns'
import {
  blankDesign, cleanDesign, ratingItems, syncParams,
  type DesignButton, type TemplateTag, type TemplateType, type ZnsTemplateDesign,
} from './zns-template-form'
import { ZnsTemplatePreview } from './zns-template-preview'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div>
        <p className="text-sm font-medium">{title}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

/** Chọn ảnh → kiểm tra + tải lên Zalo → trả media_id. Giữ ảnh xem trước ở máy, báo lên cha (`onPreview`) để khung
 * xem trước mẫu tin ở bên cạnh cũng thấy được ảnh thật (design chỉ lưu media_id, không phải URL xem được). */
function ImagePicker({
  accountId, kind, label, hint, mediaId, onChange, onPreview,
}: {
  accountId: string
  kind: 'logo' | 'image'
  label: string
  hint: string
  mediaId: string
  onChange: (mediaId: string) => void
  onPreview?: (mediaId: string, url: string) => void
}) {
  const { t } = useTranslation('marketing')
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  async function pick(file: File | undefined) {
    if (!file) return
    setBusy(true)
    try {
      const id = await uploadTemplateImage(accountId, kind, file)
      const url = URL.createObjectURL(file)
      setPreview(url)
      onChange(id)
      onPreview?.(id, url)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {mediaId ? t('znsTemplateEditor.changeImage') : t('znsTemplateEditor.chooseImage')}
        </Button>
        {preview ? (
          <img src={preview} alt="" className="h-10 max-w-[160px] rounded border object-contain" />
        ) : mediaId ? (
          <span className="text-xs text-muted-foreground">{t('znsTemplateEditor.uploadedToZalo')}</span>
        ) : null}
        {mediaId && (
          <Button type="button" variant="ghost" size="icon" aria-label={t('znsTemplateEditor.removeImage')} onClick={() => { setPreview(null); onChange('') }}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <input ref={input} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  )
}

export function ZnsTemplateEditorDialog({
  open, onOpenChange, accountId, resubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  /** Mẫu bị từ chối cần sửa và gửi duyệt lại (phải có layout = design đã lưu). */
  resubmit?: ZnsTemplate | null
}) {
  const { t } = useTranslation('marketing')
  const specQuery = useTemplateSpec()
  const spec = specQuery.data
  const submit = useSubmitZnsTemplate()

  const [design, setDesign] = useState<ZnsTemplateDesign>(() => blankDesign(1))
  const [errors, setErrors] = useState<string[]>([])
  const [warnings, setWarnings] = useState<string[]>([])
  const [headerMode, setHeaderMode] = useState<'logo' | 'images'>('logo')
  // media_id (Zalo) → blob URL cục bộ của ảnh vừa chọn trong phiên soạn này — khung xem trước dùng để hiện ảnh
  // thật (design chỉ lưu media_id, không phải URL xem lại được; mẫu resubmit có media_id cũ chưa chọn lại ảnh thì
  // khung xem trước hiện khung giữ chỗ thay vì ảnh — xem zns-template-preview.tsx).
  const [imagePreviews, setImagePreviews] = useState<Record<string, string>>({})
  const rememberPreview = (mediaId: string, url: string) => setImagePreviews((m) => ({ ...m, [mediaId]: url }))

  // Mở hộp: nạp mẫu cần sửa, hoặc khung trắng.
  useEffect(() => {
    if (!open) return
    const d = resubmit?.layout ? resubmit.layout : blankDesign(1)
    setDesign(d)
    setHeaderMode(d.header.images?.length ? 'images' : 'logo')
    setErrors([])
    setWarnings([])
    setImagePreviews({})
  }, [open, resubmit])

  // Kiểm tra qua backend khi đang soạn (chỉ một nguồn luật: cấu trúc + quy định kiểm duyệt ZBS).
  useEffect(() => {
    if (!open) return
    const handle = setTimeout(() => {
      validateTemplateDesign(cleanDesign(design)).then((r) => { setErrors(r.errors); setWarnings(r.warnings) }).catch(() => undefined)
    }, 500)
    return () => clearTimeout(handle)
  }, [design, open])

  const typeSpec = useMemo(() => spec?.templateTypes.find((t) => t.value === design.templateType), [spec, design.templateType])

  /** Sửa mẫu rồi tự đồng bộ danh sách tham số với nội dung. */
  function update(fn: (d: ZnsTemplateDesign) => ZnsTemplateDesign) {
    setDesign((prev) => {
      const next = fn(prev)
      return { ...next, params: syncParams(next) }
    })
  }

  function changeType(value: string) {
    const type = Number(value) as TemplateType
    const fresh = blankDesign(type)
    setHeaderMode('logo')
    update((d) => ({ ...fresh, templateName: d.templateName, note: d.note, header: d.header.logo ? { logo: d.header.logo } : {} }))
  }

  const buttons = design.buttons ?? []
  const paragraphs = design.paragraphs ?? []
  const table = design.table ?? []

  async function onSubmit() {
    const cleaned = cleanDesign(design)
    try {
      await submit.mutateAsync({ accountId, design: cleaned, resubmitTemplateId: resubmit?.templateId })
      toast.success(t('znsTemplateEditor.submittedToast'))
      onOpenChange(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const canSubmit = !!spec && errors.length === 0 && !submit.isPending && design.templateName.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{resubmit ? t('znsTemplateEditor.titleResubmit') : t('znsTemplateEditor.titleCreate')}</DialogTitle>
          <DialogDescription>
            {t('znsTemplateEditor.descriptionBefore')} <code className="rounded bg-muted px-1">{'<ten_tham_so>'}</code> {t('znsTemplateEditor.descriptionAfter')}
          </DialogDescription>
        </DialogHeader>

        {!spec ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_280px]">
          <ScrollArea className="max-h-[65vh] pr-3">
            <div className="space-y-4">
              <Section title={t('znsTemplateEditor.sections.general')}>
                <Field label={t('znsTemplateEditor.nameLabel')} hint={t('znsTemplateEditor.nameHint', { min: spec.limits.nameMin, max: spec.limits.nameMax })}>
                  <Input value={design.templateName} onChange={(e) => update((d) => ({ ...d, templateName: e.target.value }))} />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label={t('znsTemplateEditor.typeLabel')}>
                    <Select value={String(design.templateType)} onValueChange={changeType} disabled={!!resubmit}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {spec.templateTypes.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('znsTemplateEditor.tagLabel')} hint={t('znsTemplateEditor.tagHint')}>
                    <Select value={String(design.tag)} onValueChange={(v) => update((d) => ({ ...d, tag: Number(v) as TemplateTag }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {spec.tags.filter((t) => typeSpec?.tags.includes(t.value)).map((t) => (
                          <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </Section>

              <Section title={t('znsTemplateEditor.sections.header')} hint={t('znsTemplateEditor.headerHint')}>
                {typeSpec?.allowImages && (
                  <div className="flex gap-2">
                    {(['logo', 'images'] as const).map((m) => (
                      <Button
                        key={m} type="button" size="sm" variant={headerMode === m ? 'default' : 'outline'}
                        onClick={() => { setHeaderMode(m); update((d) => ({ ...d, header: {} })) }}
                      >
                        {m === 'logo' ? t('znsTemplateEditor.headerModeLogo') : t('znsTemplateEditor.headerModeImages')}
                      </Button>
                    ))}
                  </div>
                )}
                {headerMode === 'logo' ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <ImagePicker
                      accountId={accountId} kind="logo" label={t('znsTemplateEditor.logoLight')} hint={t('znsTemplateEditor.logoHint')}
                      mediaId={design.header.logo?.light ?? ''} onPreview={rememberPreview}
                      onChange={(id) => update((d) => ({ ...d, header: { logo: { light: id, dark: d.header.logo?.dark ?? '' } } }))}
                    />
                    <ImagePicker
                      accountId={accountId} kind="logo" label={t('znsTemplateEditor.logoDark')} hint={t('znsTemplateEditor.logoHint')}
                      mediaId={design.header.logo?.dark ?? ''} onPreview={rememberPreview}
                      onChange={(id) => update((d) => ({ ...d, header: { logo: { light: d.header.logo?.light ?? '', dark: id } } }))}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(design.header.images ?? []).map((id, i) => (
                      <ImagePicker
                        key={i} accountId={accountId} kind="image" label={t('znsTemplateEditor.imageN', { n: i + 1 })} hint={t('znsTemplateEditor.imageHint')} mediaId={id}
                        onPreview={rememberPreview}
                        onChange={(m) => update((d) => ({ ...d, header: { images: (d.header.images ?? []).map((x, k) => (k === i ? m : x)).filter((x, k) => x || k !== i) } }))}
                      />
                    ))}
                    {(design.header.images ?? []).length < 3 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => update((d) => ({ ...d, header: { images: [...(d.header.images ?? []), ''] } }))}>
                        <Plus className="h-4 w-4" /> {t('znsTemplateEditor.addImage')}
                      </Button>
                    )}
                  </div>
                )}
              </Section>

              <Section title={t('znsTemplateEditor.sections.content')}>
                {design.templateType === 2 ? (
                  <Field label={t('znsTemplateEditor.otpLabel')} hint={t('znsTemplateEditor.otpHint')}>
                    <Input value={design.otp ?? ''} onChange={(e) => update((d) => ({ ...d, otp: e.target.value }))} />
                  </Field>
                ) : (
                  <Field label={t('znsTemplateEditor.titleFieldLabel')} hint={t('znsTemplateEditor.titleFieldHint')}>
                    <Input value={design.title ?? ''} onChange={(e) => update((d) => ({ ...d, title: e.target.value }))} />
                  </Field>
                )}

                {paragraphs.map((p, i) => (
                  <Field key={i} label={t('znsTemplateEditor.paragraphN', { n: i + 1 })} hint={t('znsTemplateEditor.paragraphHint')}>
                    <div className="flex gap-2">
                      <Textarea value={p} rows={3} onChange={(e) => update((d) => ({ ...d, paragraphs: (d.paragraphs ?? []).map((x, k) => (k === i ? e.target.value : x)) }))} />
                      {design.templateType !== 2 && (
                        <Button type="button" variant="ghost" size="icon" aria-label={t('znsTemplateEditor.removeParagraph')} onClick={() => update((d) => ({ ...d, paragraphs: (d.paragraphs ?? []).filter((_, k) => k !== i) }))}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </Field>
                ))}
                {typeSpec && design.templateType !== 2 && paragraphs.length < typeSpec.maxParagraphs && (
                  <Button type="button" variant="outline" size="sm" onClick={() => update((d) => ({ ...d, paragraphs: [...(d.paragraphs ?? []), ''] }))}>
                    <Plus className="h-4 w-4" /> {t('znsTemplateEditor.addParagraph')}
                  </Button>
                )}

                {typeSpec?.allowTable && (
                  <div className="space-y-2">
                    <Label>{t('znsTemplateEditor.tableLabel')}</Label>
                    {table.map((r, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 sm:grid-cols-[1fr_1fr_140px_auto]">
                        <Input placeholder={t('znsTemplateEditor.tableRowTitle')} value={r.title} onChange={(e) => update((d) => ({ ...d, table: (d.table ?? []).map((x, k) => (k === i ? { ...x, title: e.target.value } : x)) }))} />
                        <Input placeholder={t('znsTemplateEditor.tableRowValue')} value={r.value} onChange={(e) => update((d) => ({ ...d, table: (d.table ?? []).map((x, k) => (k === i ? { ...x, value: e.target.value } : x)) }))} />
                        <Select value={String(r.rowType ?? 0)} onValueChange={(v) => update((d) => ({ ...d, table: (d.table ?? []).map((x, k) => (k === i ? { ...x, rowType: Number(v) } : x)) }))}>
                          <SelectTrigger className="hidden sm:flex"><SelectValue /></SelectTrigger>
                          <SelectContent>{spec.rowTypes.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button type="button" variant="ghost" size="icon" aria-label={t('znsTemplateEditor.removeRow')} onClick={() => update((d) => ({ ...d, table: (d.table ?? []).filter((_, k) => k !== i) }))}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {table.length < 8 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => update((d) => ({ ...d, table: [...(d.table ?? []), { title: '', value: '' }] }))}>
                        <Plus className="h-4 w-4" /> {t('znsTemplateEditor.addRow')}
                      </Button>
                    )}
                  </div>
                )}

                {design.voucher && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label={t('znsTemplateEditor.voucherName')} hint={t('znsTemplateEditor.voucherNameHint')}><Input value={design.voucher.name} onChange={(e) => update((d) => ({ ...d, voucher: { ...d.voucher!, name: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.voucherCondition')} hint={t('znsTemplateEditor.voucherConditionHint')}><Input value={design.voucher.condition} onChange={(e) => update((d) => ({ ...d, voucher: { ...d.voucher!, condition: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.voucherCode')} hint={t('znsTemplateEditor.voucherCodeHint')}><Input value={design.voucher.voucherCode} onChange={(e) => update((d) => ({ ...d, voucher: { ...d.voucher!, voucherCode: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.voucherStartDate')}><Input value={design.voucher.startDate ?? ''} onChange={(e) => update((d) => ({ ...d, voucher: { ...d.voucher!, startDate: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.voucherEndDate')} hint={t('znsTemplateEditor.voucherEndDateHint')}><Input value={design.voucher.endDate} onChange={(e) => update((d) => ({ ...d, voucher: { ...d.voucher!, endDate: e.target.value } }))} /></Field>
                  </div>
                )}

                {design.payment && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label={t('znsTemplateEditor.paymentBankCode')} hint={t('znsTemplateEditor.paymentBankCodeHint')}><Input value={design.payment.bankCode} onChange={(e) => update((d) => ({ ...d, payment: { ...d.payment!, bankCode: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.paymentAccountName')}><Input value={design.payment.accountName} onChange={(e) => update((d) => ({ ...d, payment: { ...d.payment!, accountName: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.paymentAccountNumber')}><Input value={design.payment.bankAccount} onChange={(e) => update((d) => ({ ...d, payment: { ...d.payment!, bankAccount: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.paymentAmount')} hint={t('znsTemplateEditor.paymentAmountHint')}><Input value={design.payment.amount} onChange={(e) => update((d) => ({ ...d, payment: { ...d.payment!, amount: e.target.value } }))} /></Field>
                    <Field label={t('znsTemplateEditor.paymentNote')}><Input value={design.payment.note ?? ''} onChange={(e) => update((d) => ({ ...d, payment: { ...d.payment!, note: e.target.value } }))} /></Field>
                  </div>
                )}

                {design.rating && (
                  <div className="space-y-3">
                    <Field label={t('znsTemplateEditor.ratingScale')}>
                      <Select value={design.rating.option} onValueChange={(v) => {
                        const count = spec.ratingOptions.find((o) => o.value === v)?.count ?? 5
                        update((d) => ({ ...d, rating: { option: v, items: ratingItems(count, d.rating!.items) } }))
                      }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="three_emotions">{t('znsTemplateEditor.rating3')}</SelectItem>
                          <SelectItem value="five_emotions">{t('znsTemplateEditor.rating5emo')}</SelectItem>
                          <SelectItem value="five_stars">{t('znsTemplateEditor.rating5star')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    {design.rating.items.map((it, i) => (
                      <div key={i} className="grid grid-cols-1 gap-2 rounded border p-2 sm:grid-cols-2">
                        <p className="text-xs font-medium sm:col-span-2">{t('znsTemplateEditor.ratingLevel', { rate: it.rate })}</p>
                        {([['title', t('znsTemplateEditor.ratingLevelTitle')], ['thanks', t('znsTemplateEditor.ratingThanks')], ['description', t('znsTemplateEditor.ratingDescription')], ['question', t('znsTemplateEditor.ratingQuestion')]] as const).map(([k, label]) => (
                          <Input
                            key={k} placeholder={label} value={(it as unknown as Record<string, string>)[k] ?? ''}
                            onChange={(e) => update((d) => ({ ...d, rating: { ...d.rating!, items: d.rating!.items.map((x, n) => (n === i ? { ...x, [k]: e.target.value } : x)) } }))}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {typeSpec && typeSpec.maxButtons > 0 && (
                <Section title={t('znsTemplateEditor.sections.buttons')} hint={t('znsTemplateEditor.buttonsHint', { min: typeSpec.minButtons, max: typeSpec.maxButtons })}>
                  {buttons.map((b, i) => (
                    <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[220px_1fr_1fr_auto]">
                      <Select value={String(b.type)} onValueChange={(v) => update((d) => ({ ...d, buttons: (d.buttons ?? []).map((x, k) => (k === i ? { ...x, type: Number(v) } : x)) }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{spec.buttonTypes.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input placeholder={t('znsTemplateEditor.buttonTitle')} value={b.title} onChange={(e) => update((d) => ({ ...d, buttons: (d.buttons ?? []).map((x, k) => (k === i ? { ...x, title: e.target.value } : x)) }))} />
                      <Input
                        placeholder={spec.buttonTypes.find((t) => t.value === b.type)?.kind === 'phone' ? t('znsTemplateEditor.buttonPhone') : 'https://…'}
                        value={b.content} onChange={(e) => update((d) => ({ ...d, buttons: (d.buttons ?? []).map((x, k) => (k === i ? { ...x, content: e.target.value } : x)) }))}
                      />
                      <Button type="button" variant="ghost" size="icon" aria-label={t('znsTemplateEditor.removeButton')} onClick={() => update((d) => ({ ...d, buttons: (d.buttons ?? []).filter((_, k) => k !== i) }))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {buttons.length < typeSpec.maxButtons && (
                    <Button type="button" variant="outline" size="sm" onClick={() => update((d) => ({ ...d, buttons: [...(d.buttons ?? []), { type: 1, title: '', content: '' } as DesignButton] }))}>
                      <Plus className="h-4 w-4" /> {t('znsTemplateEditor.addButton')}
                    </Button>
                  )}
                </Section>
              )}

              {design.params.length > 0 && (
                <Section title={t('znsTemplateEditor.sections.params')} hint={t('znsTemplateEditor.paramsHint')}>
                  {design.params.map((p, i) => {
                    const pt = spec.paramTypes.find((t) => t.value === p.type)
                    return (
                      <div key={p.name} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[140px_240px_1fr]">
                        <code className="truncate rounded bg-muted px-2 py-1 text-xs">{`<${p.name}>`}</code>
                        <Select value={p.type} onValueChange={(v) => setDesign((d) => ({ ...d, params: d.params.map((x, k) => (k === i ? { ...x, type: v } : x)) }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{spec.paramTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label} (≤{t.maxLength})</SelectItem>)}</SelectContent>
                        </Select>
                        <Input
                          placeholder={t('znsTemplateEditor.sampleValue')} maxLength={pt?.maxLength} value={p.sampleValue}
                          onChange={(e) => setDesign((d) => ({ ...d, params: d.params.map((x, k) => (k === i ? { ...x, sampleValue: e.target.value } : x)) }))}
                        />
                      </div>
                    )
                  })}
                </Section>
              )}

              <Field label={t('znsTemplateEditor.noteLabel')} hint={t('znsTemplateEditor.noteHint')}>
                <Textarea rows={2} maxLength={400} value={design.note ?? ''} onChange={(e) => update((d) => ({ ...d, note: e.target.value }))} />
              </Field>

              {resubmit?.rejectReason && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                  <p className="font-medium text-destructive">{t('znsTemplateEditor.rejectReasonTitle')}</p>
                  <p>{resubmit.rejectReason}</p>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="hidden sm:block">
            <div className="sticky top-0 max-h-[65vh] overflow-auto pb-1">
              <ZnsTemplatePreview design={design} spec={spec} imagePreviews={imagePreviews} />
            </div>
          </div>
          </div>
        )}

        {errors.length > 0 && (
          <ul className="max-h-24 list-disc space-y-0.5 overflow-auto rounded-md bg-destructive/5 py-2 pl-7 pr-3 text-xs text-destructive">
            {errors.slice(0, 6).map((e) => <li key={e}>{e}</li>)}
            {errors.length > 6 && <li>{t('znsTemplateEditor.andMoreErrors', { count: errors.length - 6 })}</li>}
          </ul>
        )}

        {warnings.length > 0 && (
          <ul className="max-h-24 list-disc space-y-0.5 overflow-auto rounded-md bg-warning/10 py-2 pl-7 pr-3 text-xs text-warning">
            {warnings.slice(0, 6).map((w) => <li key={w}>{w}</li>)}
            {warnings.length > 6 && <li>{t('znsTemplateEditor.andMoreWarnings', { count: warnings.length - 6 })}</li>}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('znsTemplateEditor.cancel')}</Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {resubmit ? t('znsTemplateEditor.resubmitButton') : t('znsTemplateEditor.submitButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
